import { useState, useRef, useEffect } from 'react';
import NetworkSelector from '../NetworkSelector';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher';
import WalletButton from '../WalletButton';

const roleBadgeColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  issuer: 'bg-blue-100 text-blue-700',
  verifier: 'bg-green-100 text-green-700',
  holder: 'bg-slate-100 text-slate-700',
};

type SearchResult = { type: string; labelKey: string; path: string; icon: string };

const quickLinks: SearchResult[] = [
  { type: 'page', labelKey: 'header.quickLinks.dashboard', path: '/dashboard', icon: 'dashboard' },
  { type: 'page', labelKey: 'header.quickLinks.myDids', path: '/my-dids', icon: 'badge' },
  { type: 'page', labelKey: 'header.quickLinks.aiAgents', path: '/ai-agents', icon: 'smart_toy' },
  { type: 'page', labelKey: 'header.quickLinks.schemas', path: '/schemas', icon: 'schema' },
  { type: 'page', labelKey: 'header.quickLinks.verifications', path: '/verifications', icon: 'fact_check' },
  { type: 'page', labelKey: 'header.quickLinks.didResolver', path: '/resolve', icon: 'travel_explore' },
  { type: 'page', labelKey: 'header.quickLinks.zkIdentity', path: '/zk-identity', icon: 'lock' },
  { type: 'page', labelKey: 'header.quickLinks.agentMarketplace', path: '/agent-marketplace', icon: 'storefront' },
  { type: 'page', labelKey: 'header.quickLinks.trustExplorer', path: '/trust-explorer', icon: 'verified_user' },
];

export default function Header() {
  const { user, wallet, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['layout', 'common']);
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  type ResultItem = { type: string; label: string; path: string; icon: string };
  const results: ResultItem[] = (() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const matched: ResultItem[] = [];
    quickLinks.forEach(l => {
      const label = t(l.labelKey);
      if (label.toLowerCase().includes(q)) matched.push({ type: l.type, label, path: l.path, icon: l.icon });
    });
    if (q.startsWith('did:')) {
      matched.push({
        type: 'did',
        label: t('header.resolveLabel', { query: q }),
        path: `/resolve?did=${encodeURIComponent(q)}`,
        icon: 'travel_explore',
      });
    }
    return matched;
  })();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowResults(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border-subtle bg-white px-6">
      {/* Left side */}
      <div className="flex items-center gap-4">
        <h2 className="text-sm font-bold text-text-main">{t('header.trustRegistry')}</h2>
        <div className="h-5 w-px bg-border-subtle"></div>
        <div className="relative" ref={searchRef}>
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-text-faint" style={{ fontSize: 18 }}>
            search
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results.length > 0) {
                navigate(results[0].path);
                setShowResults(false);
                setQuery('');
              }
            }}
            placeholder={t('header.searchPlaceholder')}
            className="h-10 w-full max-w-md rounded-lg border border-border-subtle bg-slate-50 pl-10 pr-4 text-sm text-text-main placeholder:text-text-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {showResults && query.trim() && (
            <div className="absolute left-0 top-12 z-50 w-full min-w-[320px] rounded-xl border border-border-subtle bg-white shadow-lg">
              {results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-text-faint">{t('header.noResultsFor', { query })}</div>
              ) : (
                results.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => { navigate(r.path); setShowResults(false); setQuery(''); }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 first:rounded-t-xl last:rounded-b-xl"
                  >
                    <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 18 }}>{r.icon}</span>
                    <span className="font-medium text-text-main">{r.label}</span>
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-text-faint">{r.type}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Network selector — the user picks which chain they operate on */}
        <NetworkSelector />

        <div className="h-5 w-px bg-border-subtle"></div>

        {/* Language switcher */}
        <LanguageSwitcher />

        <div className="h-5 w-px bg-border-subtle"></div>

        {/* MetaMask wallet connect */}
        <WalletButton />

        <div className="h-5 w-px bg-border-subtle"></div>

        {/* Wallet address */}
        {wallet && (
          <button onClick={() => navigator.clipboard.writeText(wallet.address)}
            className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-mono text-text-faint hover:bg-slate-200"
            title={t('header.copyWallet', { address: wallet.address })}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>account_balance_wallet</span>
            {wallet.address.slice(0, 10)}...{wallet.address.slice(-4)}
          </button>
        )}

        {/* User info */}
        {user && (
          <div className="flex items-center gap-3">
            {(user as any).avatarUrl ? (
              <img src={(user as any).avatarUrl} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
            )}
            <span className="text-sm font-bold text-text-main">{user.name}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${roleBadgeColors[user.role] || roleBadgeColors.holder}`}>
              {user.role}
            </span>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold text-text-faint transition-colors hover:bg-slate-50 hover:text-text-main"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>logout</span>
              {t('header.logout')}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
